import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import EmrShowcase from './EmrShowcase';

const Hero = () => {
  const { t } = useTranslation();

  return (
    <section id="hero" className="bf-hero-section">
      <div className="bf-hero-glow" aria-hidden="true" />
      <div className="bf-container bf-hero-container">
        <div className="bf-hero-viewport">
          <div className="bf-hero-copy">
            <span className="bf-hero-pill">
              <span className="dot"></span>
              {t('hero.eyebrow', 'Clinical reasoning, built into your EMR')}
            </span>

            <h1 className="bf-hero-headline">
              {t('hero.titleLead', 'Invisible clinical reasoning, ')}
              <span className="bf-grad-text">
                {t('hero.titleEmphasis', 'inside every EMR.')}
              </span>
            </h1>

            <p className="bf-lead bf-hero-lead">
              {t(
                'hero.description',
                'A quiet layer of clinical intelligence that helps your teams see the whole patient, sooner.'
              )}
            </p>

            <div className="bf-hero-ctas">
              <a
                href="https://calendly.com/tiago-costa-bloodflow"
                target="_blank"
                rel="noopener noreferrer"
                className="bf-btn bf-btn-primary bf-btn-lg"
              >
                {t('hero.cta', 'Schedule a demo')} <span className="bf-arrow">→</span>
              </a>
              <Link to="/contact-sales" className="bf-btn bf-btn-ghost bf-btn-lg">
                {t('hero.cta2', 'Contact us')}
              </Link>
            </div>
          </div>

          <span className="bf-hero-scroll-hint" aria-hidden="true">
            {t('hero.scrollHint', 'See it inside your EMR')} <span className="bf-hero-scroll-arrow">↓</span>
          </span>
        </div>

        <div className="bf-hero-showcase">
          <EmrShowcase />
        </div>
      </div>

      <style>{`
        .bf-hero-section {
          padding-top: 0;
          padding-bottom: 72px;
          position: relative;
          overflow: clip;
        }
        .bf-hero-glow {
          position: absolute;
          top: -240px; left: 50%; transform: translateX(-50%);
          width: 1100px; height: 760px;
          background: radial-gradient(50% 50% at 50% 50%, rgba(240,31,61,0.30) 0%, rgba(240,31,61,0.08) 38%, transparent 72%);
          filter: blur(10px);
          pointer-events: none; z-index: 0;
        }
        .bf-hero-container { position: relative; z-index: 1; width: 100%; }

        .bf-hero-viewport {
          min-height: 100vh;
          box-sizing: border-box;
          padding: 120px 0 56px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          position: relative;
        }
        .bf-hero-copy {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          max-width: 900px;
          margin: 0 auto;
        }
        .bf-hero-pill {
          display: inline-flex; align-items: center; gap: 9px;
          padding: 7px 15px 7px 13px;
          border: 1px solid var(--bf-rule-2);
          border-radius: 999px;
          background: rgba(255,255,255,0.03);
          backdrop-filter: blur(8px);
          font-family: var(--bf-mono);
          font-size: 11px; letter-spacing: 0.1em; text-transform: uppercase;
          color: var(--bf-ink-2);
        }
        .bf-hero-pill .dot {
          width: 6px; height: 6px; border-radius: 50%; background: var(--bf-accent);
          box-shadow: 0 0 10px 1px rgba(240,31,61,0.9);
        }
        .bf-hero-headline {
          font-size: clamp(40px, 6vw, 76px) !important;
          line-height: 1.03 !important;
          letter-spacing: -0.04em !important;
          font-weight: 650 !important;
          margin-top: 26px;
          padding-bottom: 0.08em;
          max-width: 18ch;
        }
        .bf-hero-headline .bf-grad-text { padding-bottom: 0.1em; }
        .bf-hero-lead {
          font-size: 19px !important;
          color: var(--bf-muted);
          max-width: 54ch;
          margin-top: 24px;
        }
        .bf-hero-ctas { display: flex; gap: 12px; flex-wrap: wrap; justify-content: center; margin-top: 34px; }

        .bf-hero-scroll-hint {
          position: absolute;
          bottom: 28px;
          left: 50%;
          transform: translateX(-50%);
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-family: var(--bf-mono);
          font-size: 10.5px;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: var(--bf-muted-2);
          white-space: nowrap;
        }
        .bf-hero-scroll-arrow { animation: bf-hero-bob 1.8s ease-in-out infinite; }
        @keyframes bf-hero-bob { 0%,100% { transform: translateY(0); } 50% { transform: translateY(4px); } }

        .bf-hero-showcase {
          margin: 40px auto 0;
          width: 100%;
          max-width: 1140px;
          position: relative;
          z-index: 1;
        }

        @media (max-width: 900px) {
          .bf-hero-section { padding-bottom: 48px; }
          .bf-hero-viewport { min-height: 100svh; padding: 108px 0 56px; }
          .bf-hero-showcase { margin-top: 32px; }
        }
      `}</style>
    </section>
  );
};

export default Hero;
